/*
 * Vajihi Labs - Email Signature Builder
 * Apply view: reviews the signature you built in Customize — no separate
 * personal-details form here anymore, since Customize is already "your own
 * info." Apply shows the resolved result, honest readiness, and the actual
 * insert/copy/download actions. "Edit signature" always goes back to
 * Customize without losing anything, since both views read the same
 * shared model.
 */

/* global document, navigator */

import { getModel } from "./lib/appState";
import { BASIC_THEME, isLogoAndLinksReady, renderTheme, THEMES, validateForApply } from "./lib/themes";

function themeMetaFor(id: string) {
  return id === "basic" ? BASIC_THEME : THEMES.find((t) => t.id === id);
}
import { hasLocalLogoPreview, withLocalLogoOverride } from "./lib/logoPreviewState";
import { renderScaledPreview } from "./lib/scale";
import { downloadSignatureHtml } from "./lib/download";
import { insertSignature, isStandalonePreview } from "./lib/outlookIntegration";

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setText(id: string, text: string, isError = false): void {
  const el = byId<HTMLElement>(id);
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("status--error", isError);
}

function renderReadiness(): void {
  const model = getModel();
  const issues = validateForApply(model);
  const logoLinksReady = isLogoAndLinksReady(model, hasLocalLogoPreview());

  const nameEl = byId<HTMLElement>("summary-person-name");
  const emailEl = byId<HTMLElement>("summary-person-email");
  if (nameEl) nameEl.textContent = model.person.name.trim() || "No name yet";
  if (emailEl) emailEl.textContent = model.person.email.trim() || "No email yet";

  const themeMeta = themeMetaFor(model.themeId);
  const themeNameEl = byId<HTMLElement>("summary-theme-name");
  if (themeNameEl) themeNameEl.textContent = themeMeta?.name ?? model.themeId;
  const themeLabelEl = byId<HTMLElement>("apply-theme-label");
  if (themeLabelEl) themeLabelEl.textContent = themeMeta?.name ?? model.themeId;

  const logoStatusEl = byId<HTMLElement>("summary-logo-status");
  if (logoStatusEl) {
    logoStatusEl.textContent = logoLinksReady ? "Ready" : "Needs setup";
    logoStatusEl.className = `summary-row__secondary ${logoLinksReady ? "summary-row__secondary--ready" : "summary-row__secondary--needs-setup"}`;
  }

  const insertBtn = byId<HTMLButtonElement>("apply-insert-btn");
  const blocking = issues.length > 0;
  if (insertBtn) {
    insertBtn.disabled = blocking || isStandalonePreview();
  }
  if (isStandalonePreview()) {
    setText("apply-insert-status", "Preview mode — open this inside Outlook to insert a signature into an email.", true);
  } else if (blocking) {
    setText("apply-insert-status", issues.map((i) => i.message).join(" "), true);
  } else {
    setText("apply-insert-status", "");
  }
}

function renderPreview(): void {
  const model = getModel();
  const wrapper = byId<HTMLElement>("apply-preview")?.parentElement;
  const stage = byId<HTMLElement>("apply-preview");
  if (wrapper && stage) {
    renderScaledPreview(wrapper, stage, renderTheme(withLocalLogoOverride(model)));
  }
  renderReadiness();
}

async function handleCopy(): Promise<void> {
  const model = getModel();
  const issues = validateForApply(model);
  if (issues.length > 0) {
    setText("apply-copy-status", issues.map((i) => i.message).join(" "), true);
    return;
  }
  const html = renderTheme(model);
  try {
    await navigator.clipboard.writeText(html);
    setText("apply-copy-status", "Copied the HTML — paste it into Outlook's own signature settings.");
    return;
  } catch {
    // fall back below
  }
  const temp = document.createElement("textarea");
  temp.value = html;
  temp.style.position = "fixed";
  temp.style.opacity = "0";
  document.body.appendChild(temp);
  temp.focus();
  temp.select();
  try {
    const ok = document.execCommand("copy");
    setText("apply-copy-status", ok ? "Copied the HTML — paste it into Outlook's own signature settings." : "Couldn't auto-copy this HTML.", !ok);
  } catch {
    setText("apply-copy-status", "Couldn't auto-copy this HTML.", true);
  } finally {
    document.body.removeChild(temp);
  }
}

function handleDownload(): void {
  const model = getModel();
  const issues = validateForApply(model);
  if (issues.length > 0) {
    setText("apply-download-status", issues.map((i) => i.message).join(" "), true);
    return;
  }
  const html = renderTheme(model);
  const result = downloadSignatureHtml(html, "signature.html");
  setText("apply-download-status", result.message, !result.ok);
}

async function handleInsert(): Promise<void> {
  const model = getModel();
  const issues = validateForApply(model);
  if (issues.length > 0) {
    setText("apply-insert-status", issues.map((i) => i.message).join(" "), true);
    return;
  }
  const html = renderTheme(model);
  const btn = byId<HTMLButtonElement>("apply-insert-btn");
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Inserting…";
  }
  const result = await insertSignature(html);
  if (btn) {
    btn.disabled = false;
    btn.textContent = "Insert signature";
  }
  setText("apply-insert-status", result.message, !result.ok);
  if (result.ok) renderReadiness();
}

export function initApply(callbacks: {
  onEditSignature: () => void;
  onEditPerson: () => void;
  onEditTheme: () => void;
  onEditLogoLinks: () => void;
  onNeedHelp: () => void;
  onExpand: () => void;
}): void {
  byId<HTMLButtonElement>("edit-signature-link")?.addEventListener("click", callbacks.onEditSignature);
  byId<HTMLButtonElement>("summary-row-person")?.addEventListener("click", callbacks.onEditPerson);
  byId<HTMLButtonElement>("summary-row-theme")?.addEventListener("click", callbacks.onEditTheme);
  byId<HTMLButtonElement>("summary-row-logo")?.addEventListener("click", callbacks.onEditLogoLinks);
  byId<HTMLButtonElement>("apply-help-link")?.addEventListener("click", callbacks.onNeedHelp);
  byId<HTMLButtonElement>("apply-expand-btn")?.addEventListener("click", callbacks.onExpand);

  byId<HTMLButtonElement>("apply-copy-btn")?.addEventListener("click", () => {
    void handleCopy();
  });
  byId<HTMLButtonElement>("apply-download-btn")?.addEventListener("click", handleDownload);
  byId<HTMLButtonElement>("apply-insert-btn")?.addEventListener("click", () => {
    void handleInsert();
  });
}

/** Called every time Apply becomes the active view — "Entering Apply
 *  validates the committed data; it does not insert anything automatically." */
export function activateApply(): void {
  renderPreview();
}

export function getApplyPreviewHtml(): string {
  return renderTheme(withLocalLogoOverride(getModel()));
}
